/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { UserReadAll } from "src/store/api/generatedApi";
import { MenuItem } from "src/dashboard/menuItems/types";
import { getMenuItemPermissions } from "src/dashboard/menuItems/utils";

interface UserAccessDialogProps {
  open: boolean;
  menuItem: MenuItem | null;
  users: UserReadAll[];
  onClose: () => void;
}

export const UserAccessDialog: React.FC<UserAccessDialogProps> = ({
  open,
  menuItem,
  users,
  onClose,
}) => {
  const { t } = useTranslation();
  const permissions = menuItem ? getMenuItemPermissions(menuItem) : [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t("usersWithAccessTo", {
          menuItem: t(menuItem?.i18nKey ?? ""),
        })}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" component="p">
          {t("path")}: {menuItem?.path}
        </Typography>
        {permissions.length > 0 ? (
          <Typography variant="body2" color="text.secondary" component="p">
            {t("requiredPermissions")}
            {permissions.join(", ")}
          </Typography>
        ) : null}
        {menuItem?.featureFlag ? (
          <Typography variant="body2" color="text.secondary" component="p">
            {t("featureFlagLabel")} {menuItem.featureFlag}
          </Typography>
        ) : null}

        <List>
          {users.map((user) => (
            <ListItem key={user.uid}>
              <ListItemAvatar>
                <Avatar>{user.name?.charAt(0) ?? "?"}</Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={user.name}
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {user.is_admin ? (
                        <Chip
                          label={t("admin")}
                          size="small"
                          color="primary"
                          sx={{ mr: 1 }}
                        />
                      ) : null}
                      {!user.is_enabled && (
                        <Chip
                          label={t("disabled")}
                          size="small"
                          color="error"
                          sx={{ mr: 1 }}
                        />
                      )}
                      {user.permissions?.map((perm) => (
                        <Chip
                          key={perm.permission}
                          label={perm.permission}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>

        {users.length === 0 && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            {t("noUsersHaveAccess")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("close")}</Button>
      </DialogActions>
    </Dialog>
  );
};
